/* File: chatNavLink.jsx
 * Path: ananunadm/src/components/main/chatNavLink.jsx
*/
import React from "react";
import { NavLink, Avatar, Text, Badge, Indicator, Flex, ActionIcon } from "@mantine/core";
import parse from "html-react-parser";
import { IconBan, IconBellOff, IconCheck, IconChecks, IconBuilding, IconPin, IconChevronDown } from "@tabler/icons-react";
import moment from "moment";
import { getEntityImageSrc, getUserImageSrc } from "../../helpers/uploadImage";
import { getMessageReadReceiptState } from "../chat/chatMessageUtils";
import { isChatMuted, isChatBlockedByUser, hasUnread, getUnreadCount } from "./sidebarChatUtils";
import { T } from "../../helpers/translator";

/**
 * Reusable chat navigation link component
 * @param {Object} props
 * @param {Object} props.item - Chat item object
 * @param {number} props.index - Index for activePage calculation
 * @param {number} props.baseIndex - Base index offset (e.g., channelChats.length)
 * @param {string} props.route - Navigation route (e.g., "/chat/" or "/chat/chat-group/")
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.active - Active state
 * @param {Object} props.user - Current user object
 * @param {string} props.lang - Current language
 * @param {Array} props.connectedUsers - Array of connected users with status
 * @param {boolean} props.showLastMessage - Whether to show last message details
 * @param {boolean} props.showLastMessageTimestamp - Whether to show the last message date/time
 * @param {boolean} props.compact - Whether to use compact row spacing and avatar sizing
 */
const ChatNavLink = ({
    item,
    index,
    baseIndex = 0,
    route,
    onClick,
    active,
    user,
    lang,
    connectedUsers = [],
    showLastMessage = false,
    showLastMessageTimestamp = true,
    compact = false,
    onContextMenu,
    onOpenMenu,
    showTypeBadge = false,
    styles
}) => {
    const isChannel = item.chatType === "channel";
    const isGroup = item.chatType === "group";
    const isDirect = item.chatType === "direct";

    const currentUserId = String(user?._id || "");

    // Find partner user for direct chats
    const partner = isDirect
        ? item.users?.find((usr) => {
            const participantId = String(usr?._id || usr || "");
            return participantId && participantId !== currentUserId;
        })
        : null;

    // Get connection status and in-office presence for direct chats
    const partnerConnected = partner ? connectedUsers.find((u) => u._id === partner?._id) : null;
    const userConnectionStatus = partnerConnected?.connectionStatus ?? null;
    const userInOffice = partnerConnected?.inOffice ?? false;

    // Determine display label
    const label = isDirect
        ? partner?.name?.[lang] || "Unknown"
        : item.name?.charAt(0).toUpperCase() + item.name?.slice(1) || "Chat";

    // Type badge (only inside the Pinned/Archived groups, where mixed types live)
    const typeBadge = showTypeBadge ? (
        <Badge
            size="xs"
            radius="sm"
            variant="light"
            color={isDirect ? "teal" : isChannel ? "grape" : "blue"}
        >
            {T(isDirect ? "chatBadgeDirect" : isChannel ? "chatBadgeChannel" : "chatBadgeGroup", lang)}
        </Badge>
    ) : null;

    const labelNode = typeBadge ? (
        <Flex gap={6} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            <Text component="span" truncate style={{ minWidth: 0 }}>
                {label}
            </Text>
            {typeBadge}
        </Flex>
    ) : (
        <Text component="span" truncate style={{ display: "block", minWidth: 0 }}>
            {label}
        </Text>
    );

    // Determine avatar source
    const avatarSrc = isDirect
        ? getUserImageSrc(partner, { targetWidth: compact ? 32 : 40 })
        : getEntityImageSrc(item, { targetWidth: compact ? 32 : 40 });
    const avatarSize = compact ? "sm" : "md";

    // Icon slot (with indicator for direct chats)
    const icon = isDirect ? (
        <div style={{ position: "relative", display: "inline-flex" }}>
            <Indicator
                inline
                processing={userConnectionStatus === "online"}
                color={
                    userConnectionStatus === "online"
                        ? "green"
                        : userConnectionStatus === "away"
                            ? "orange"
                            : "grey"
                }
                position="top-start"
                size={compact ? 8 : 10}
            >
                <Avatar size={avatarSize} radius="xl" src={avatarSrc} />
            </Indicator>
            {userInOffice && (
                <div style={{
                    position: "absolute",
                    bottom: compact ? -1 : -2,
                    right: compact ? -1 : -2,
                    background: "#228be6",
                    borderRadius: "50%",
                    width: compact ? 10 : 12,
                    height: compact ? 10 : 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1.5px solid white",
                }}>
                    <IconBuilding size={compact ? 6 : 7} color="white" stroke={2.5} />
                </div>
            )}
        </div>
    ) : (
        <Avatar size={avatarSize} radius="xl" src={avatarSrc} />
    );

    const lastMessageCreatedAt = item.lastMessage?.created || item.lastMessage?.createdAt;
    const lastMessageDateLabel = lastMessageCreatedAt
        ? moment(lastMessageCreatedAt).isSame(new Date(), "day")
            ? moment(lastMessageCreatedAt).format("HH:mm")
            : moment(lastMessageCreatedAt).format("DD/MM")
        : "";

    // Right section with date/time, unread badge, and the menu trigger arrow
    const rightSection = (
        <Flex align="center" gap={4} wrap="nowrap">
            <Flex direction="column" align="flex-end" gap={compact ? 2 : 4}>
                <Flex gap={4} align="center">
                    {isChatBlockedByUser(item, currentUserId) ? <IconBan size={13} color="#fa5252" /> : null}
                    {item.userState?.pinned ? <IconPin size={13} color="#228be6" /> : null}
                    {isChatMuted(item.userState) ? <IconBellOff size={13} color="#868e96" /> : null}
                </Flex>
                {showLastMessageTimestamp && lastMessageDateLabel ? (
                    <Text size="xs" color="dimmed" style={{ whiteSpace: "nowrap" }}>
                        {lastMessageDateLabel}
                    </Text>
                ) : null}
                {hasUnread(item) ? (
                    getUnreadCount(item) > 0 ? (
                        <Badge variant="light" color="blue" size="sm">
                            {getUnreadCount(item)}
                        </Badge>
                    ) : (
                        <Badge
                            variant="filled"
                            color="blue"
                            size="sm"
                            style={{ width: 10, height: 10, minWidth: 10, padding: 0, borderRadius: "50%" }}
                        />
                    )
                ) : null}
            </Flex>
            {onOpenMenu ? (
                <ActionIcon
                    component="div"
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Chat menu"
                    // Keep this mousedown from reaching Mantine's document-level
                    // click-outside listener, which would pre-close the menu on
                    // mousedown and defeat the click-to-toggle below.
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => onOpenMenu(event, item)}
                    onContextMenu={(event) => onOpenMenu(event, item)}
                >
                    <IconChevronDown size={16} />
                </ActionIcon>
            ) : null}
        </Flex>
    );

    const lastMessageSenderId = item.lastMessage?.from?._id || item.lastMessage?.from;
    const isLastMessageFromCurrentUser = String(lastMessageSenderId) === String(user._id);
    const lastMessageReceiptState = getMessageReadReceiptState({
        message: item.lastMessage,
        participants: [
            ...(Array.isArray(item.users) ? item.users : []),
            ...(Array.isArray(item.admins) ? item.admins : [])
        ],
        currentUserId: user._id
    });
    const shouldShowReadReceiptIcon = isLastMessageFromCurrentUser && lastMessageReceiptState.recipientIds.length > 0;

    const readReceiptIcon = shouldShowReadReceiptIcon ? (
        lastMessageReceiptState.allRecipientsRead ? (
            <IconChecks size={16} color="#2f9e44" />
        ) : (
            <IconCheck size={16} color="#868e96" />
        )
    ) : null;

    const lastMessagePreview = item.lastMessage?.previewText || item.lastMessage?.body || "";
    const description = showLastMessage && item.lastMessage && (
        <Flex gap="xs" justify="space-between" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            {readReceiptIcon ? <span style={{ flexShrink: 0, display: "inline-flex" }}>{readReceiptIcon}</span> : null}
            <Text component="span" mr={12} color="dimmed" className="parsed-content" truncate style={{ flex: 1, minWidth: 0 }}>
                {parse(lastMessagePreview, { trim: true }) || "No message"}
            </Text>
        </Flex>
    );

    return <NavLink
        label={labelNode}
        onClick={onClick}
        onContextMenu={(event) => onContextMenu?.(event, item)}
        p={compact ? 6 : 12}
        icon={icon}
        rightSection={rightSection}
        active={active}
        key={baseIndex + index}
        description={description}
        variant="subtle"
        styles={{
            ...styles,
            body: { ...styles?.body, minWidth: 0, overflow: "hidden" },
            label: {
                ...styles?.label,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            },
            description: {
                ...styles?.description,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            },
            rightSection: { ...styles?.rightSection, flexShrink: 0 }
        }}
    />
};

export default ChatNavLink;
